import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChangePasswordForm } from "./change-password-form";
import {
    useReauthenticateUser,
    useVerifyReauthenticationMfa,
} from "../../hooks/use-reauthenticate-user";
import { VerifyMfaForm } from "./verify-mfa-form";

const schema = yup
    .object({
        password: yup.string().required("Please enter your current password"),
    })
    .required();

const codeSchema = yup
    .object({
        code: yup
            .string()
            .required("Please enter the code")
            .matches(/^\d{6}$/, "Enter the 6-digit code"),
    })
    .required();

export const ReauthenticateUser = ({ onReauthenticated }) => {
    const [resolver, setResolver] = useState(null);

    const methods = useForm({
        resolver: yupResolver(schema),
    });

    const codeMethods = useForm({
        resolver: yupResolver(codeSchema),
    });

    const mutation = useReauthenticateUser();
    const verifyMutation = useVerifyReauthenticationMfa(onReauthenticated);

    const onSubmit = (data) => {
        mutation.mutate(data, {
            onSuccess: (result) => {
                if (result?.mfaRequired) {
                    setResolver(result.resolver);
                    return;
                }

                onReauthenticated();
            },
        });
    };

    const onVerify = ({ code }) => {
        verifyMutation.mutate({
            resolver,
            code,
        });
    };

    if (resolver) {
        return (
            <div className="grid grid-cols-12 items-start gap-y-6">
                <Card size="lg" className="grid grid-cols-12 gap-y-8">
                    <div className="col-span-full">
                        <h2 className="mb-3 text-xl font-semibold">
                            Enter your verification code
                        </h2>

                        <p className="text-sm">
                            Enter the 6-digit code from your authenticator app.
                        </p>
                    </div>

                    <VerifyMfaForm
                        methods={codeMethods}
                        onSubmit={onVerify}
                        isPending={verifyMutation.isPending}
                    />

                    <div className="col-span-full">
                        <Button variant="secondary" size="sm" asChild>
                            <Link to=".." relative="path">
                                Go back
                            </Link>
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-12 items-start gap-y-6">
            <Card size="lg" className="grid grid-cols-12 gap-y-8">
                <div className="col-span-full">
                    <h2 className="mb-3 text-xl font-semibold">
                        Enter your current password
                    </h2>
                </div>

                <ChangePasswordForm
                    methods={methods}
                    onSubmit={onSubmit}
                    isPending={mutation.isPending}
                />

                <div className="col-span-full">
                    <Button variant="secondary" size="sm" asChild>
                        <Link to=".." relative="path">
                            Go back
                        </Link>
                    </Button>
                </div>
            </Card>
        </div>
    );
};
